import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Phone,
  Mic,
  Play,
  Pause,
  Volume2,
  VolumeX,
  Clock,
  Users,
  TrendingUp,
  TrendingDown,
  BarChart3,
  PieChart,
  Activity,
  Target,
  AlertTriangle,
  CheckCircle,
  XCircle,
  RefreshCw,
  Download,
  Filter,
  Calendar,
  Eye,
  EyeOff,
  Headphones,
  PhoneCall,
  Voicemail,
  MessageSquare,
  DollarSign,
  Percent,
  ArrowUp,
  ArrowDown,
  PlayCircle,
  PauseCircle,
  Settings,
  Info,
  Signal,
  Zap
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button/Button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/Select';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  TimeScale,
  Filler
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import 'chartjs-adapter-date-fns';
import { useTheme } from '../../hooks/useTheme';
import { useWebSocket } from '../../hooks/useWebSocket';
import { cn } from '../../utils/cn';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

interface VoicemailAnalyticsDashboardProps {
  campaignId: string;
  className?: string;
}

interface VoicemailMetrics {
  totalDrops: number;
  delivered: number;
  failed: number;
  listened: number;
  partialListens: number;
  completionRate: number;
  listenRate: number;
  averageListenDuration: number;
  totalCost: number;
  costPerDrop: number;
  costPerListen: number;
  engagementScore: number;
}

interface RealTimeMetrics {
  timestamp: Date;
  drops: number;
  delivered: number;
  listened: number;
  dropVelocity: number;
  listenVelocity: number;
}

interface AudioAnalysis {
  averageDuration: number;
  optimalDuration: number;
  durationBreakdown: Array<{
    range: string;
    count: number;
    listenRate: number;
    completionRate: number;
  }>;
  qualityMetrics: {
    averageBitrate: number;
    averageFileSize: number;
    formatDistribution: Array<{
      format: string;
      count: number;
      deliveryRate: number;
    }>;
  };
}

interface EngagementAnalysis {
  listenBehavior: {
    immediateListens: number;
    delayedListens: number;
    averageTimeToListen: number;
  };
  completionAnalysis: {
    fullListens: number;
    partialListens: number;
    averageCompletionPercentage: number;
    dropOffPoints: Array<{
      timePoint: number;
      dropOffRate: number;
    }>;
  };
}

interface CarrierPerformance {
  carrier: string;
  drops: number;
  delivered: number;
  failed: number;
  deliveryRate: number;
  avgDeliveryTime: number;
  status: 'healthy' | 'degraded' | 'critical';
}

interface Recommendation {
  category: 'audio' | 'timing' | 'targeting' | 'duration' | 'delivery';
  priority: 'critical' | 'high' | 'medium' | 'low';
  title: string;
  description: string;
  expectedImpact: string;
  actionSteps: string[];
  effort: 'low' | 'medium' | 'high';
}

const VoicemailAnalyticsDashboard: React.FC<VoicemailAnalyticsDashboardProps> = ({
  campaignId,
  className = ''
}) => {
  const [metrics, setMetrics] = useState<VoicemailMetrics | null>(null);
  const [realTimeMetrics, setRealTimeMetrics] = useState<RealTimeMetrics | null>(null);
  const [audioAnalysis, setAudioAnalysis] = useState<AudioAnalysis | null>(null);
  const [engagementAnalysis, setEngagementAnalysis] = useState<EngagementAnalysis | null>(null);
  const [carrierPerformance, setCarrierPerformance] = useState<CarrierPerformance[]>([]);
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeRange, setTimeRange] = useState('24h');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [selectedView, setSelectedView] = useState<'overview' | 'audio' | 'engagement' | 'carriers'>('overview');

  // Fetch comprehensive analytics
  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      
      // Handle demo campaigns by providing mock data
      if (campaignId.startsWith('demo-')) {
        const mockMetrics: VoicemailMetrics = {
          totalDrops: 8940,
          delivered: 8520,
          failed: 420,
          listened: 3680,
          partialListens: 1240,
          completionRate: 0.748,
          listenRate: 0.432,
          averageListenDuration: 42.8,
          totalCost: 894.00,
          costPerDrop: 0.10,
          costPerListen: 0.24,
          engagementScore: 78.5
        };

        const mockAudioAnalysis: AudioAnalysis = {
          averageDuration: 58.2,
          optimalDuration: 45.0,
          durationBreakdown: [
            { range: '0-15s', count: 892, listenRate: 0.82, completionRate: 0.95 },
            { range: '15-30s', count: 2340, listenRate: 0.74, completionRate: 0.89 },
            { range: '30-45s', count: 3120, listenRate: 0.68, completionRate: 0.82 },
            { range: '45-60s', count: 1890, listenRate: 0.45, completionRate: 0.72 },
            { range: '60s+', count: 698, listenRate: 0.32, completionRate: 0.58 }
          ],
          qualityMetrics: {
            averageBitrate: 128,
            averageFileSize: 1.2,
            formatDistribution: [
              { format: 'MP3', count: 6820, deliveryRate: 0.982 },
              { format: 'WAV', count: 1890, deliveryRate: 0.975 },
              { format: 'M4A', count: 230, deliveryRate: 0.956 }
            ]
          }
        };

        const mockEngagementAnalysis: EngagementAnalysis = {
          listenBehavior: {
            immediateListens: 1240,
            delayedListens: 2440,
            averageTimeToListen: 8.5
          },
          completionAnalysis: {
            fullListens: 2750,
            partialListens: 930,
            averageCompletionPercentage: 74.8,
            dropOffPoints: [
              { timePoint: 10, dropOffRate: 0.12 },
              { timePoint: 25, dropOffRate: 0.18 },
              { timePoint: 40, dropOffRate: 0.25 },
              { timePoint: 55, dropOffRate: 0.35 }
            ]
          }
        };

        const mockCarrierPerformance: CarrierPerformance[] = [
          {
            carrier: 'Verizon',
            drops: 2680,
            delivered: 2580,
            failed: 100,
            deliveryRate: 0.963,
            avgDeliveryTime: 1.8,
            status: 'healthy'
          },
          {
            carrier: 'AT&T',
            drops: 2290,
            delivered: 2210,
            failed: 80,
            deliveryRate: 0.965,
            avgDeliveryTime: 2.1,
            status: 'healthy'
          },
          {
            carrier: 'T-Mobile',
            drops: 2140,
            delivered: 2020,
            failed: 120,
            deliveryRate: 0.944,
            avgDeliveryTime: 2.8,
            status: 'degraded'
          },
          {
            carrier: 'Sprint',
            drops: 1830,
            delivered: 1710,
            failed: 120,
            deliveryRate: 0.934,
            avgDeliveryTime: 3.2,
            status: 'degraded'
          }
        ];

        const mockRecommendations: Recommendation[] = [
          {
            category: 'duration',
            priority: 'high',
            title: 'Optimize Message Length',
            description: 'Messages under 45 seconds show 68% better completion rates than longer messages.',
            expectedImpact: '+25% completion rate improvement',
            actionSteps: [
              'Keep core message under 30 seconds',
              'Include key value proposition early',
              'Test different script lengths'
            ],
            effort: 'medium'
          },
          {
            category: 'audio',
            priority: 'medium',
            title: 'Improve Audio Quality',
            description: 'Higher bitrate audio (256kbps) shows 15% better engagement than current 128kbps.',
            expectedImpact: '+12% listen rate boost',
            actionSteps: [
              'Upgrade recording quality to 256kbps',
              'Use professional voice talent',
              'Implement noise reduction'
            ],
            effort: 'low'
          },
          {
            category: 'timing',
            priority: 'medium',
            title: 'Adjust Drop Schedule',
            description: 'Voicemail drops between 9-10 AM show 34% higher listen rates.',
            expectedImpact: '+20% listen rate increase',
            actionSteps: [
              'Schedule drops for 9:00-10:00 AM window',
              'Avoid late afternoon drops',
              'Test weekday vs weekend performance'
            ],
            effort: 'low'
          }
        ];

        // Simulate API delay
        await new Promise(resolve => setTimeout(resolve, 900));
        
        setMetrics(mockMetrics);
        setAudioAnalysis(mockAudioAnalysis);
        setEngagementAnalysis(mockEngagementAnalysis);
        setCarrierPerformance(mockCarrierPerformance);
        setRecommendations(mockRecommendations);
        setError(null);
        setLoading(false);
        return;
      }

      const endDate = new Date();
      const startDate = new Date();
      
      switch (timeRange) {
        case '1h':
          startDate.setHours(startDate.getHours() - 1);
          break;
        case '24h':
          startDate.setDate(startDate.getDate() - 1);
          break;
        case '7d':
          startDate.setDate(startDate.getDate() - 7);
          break;
        case '30d':
          startDate.setDate(startDate.getDate() - 30);
          break;
      }

      const response = await fetch(
        `/api/analytics/voicemail/campaigns/${campaignId}/comprehensive?` +
        `startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}&includePredictions=true`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch analytics');
      }

      const data = await response.json();
      setMetrics(data.data.summary);
      setAudioAnalysis(data.data.audioAnalysis);
      setEngagementAnalysis(data.data.engagementAnalysis);
      setCarrierPerformance(data.data.deliveryAnalysis.carrierPerformance);
      setRecommendations(data.data.recommendations);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  // Fetch real-time metrics
  const fetchRealTimeMetrics = async () => {
    try {
      // Handle demo campaigns with mock real-time data
      if (campaignId.startsWith('demo-')) {
        const mockRealTimeMetrics: RealTimeMetrics = {
          timestamp: new Date(),
          drops: Math.floor(Math.random() * 8) + 3,
          delivered: Math.floor(Math.random() * 6) + 2,
          listened: Math.floor(Math.random() * 3) + 1,
          dropVelocity: Math.random() * 3 + 1,
          listenVelocity: Math.random() * 1.5 + 0.3
        };
        
        setRealTimeMetrics(mockRealTimeMetrics);
        return;
      }

      const response = await fetch(`/api/analytics/voicemail/campaigns/${campaignId}/realtime`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setRealTimeMetrics(data.data.liveUpdates);
      }
    } catch (err) {
      console.error('Failed to fetch real-time metrics:', err);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, [campaignId, timeRange]);

  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(fetchRealTimeMetrics, 30000); // Every 30 seconds
      return () => clearInterval(interval);
    }
  }, [autoRefresh, campaignId]);

  const formatNumber = (num: number): string => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const formatPercentage = (value: number): string => {
    return `${(value * 100).toFixed(1)}%`;
  };

  const formatDuration = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy': return 'text-green-400';
      case 'degraded': return 'text-yellow-400';
      case 'critical': return 'text-red-400';
      default: return 'text-gray-400';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'high': return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
      case 'medium': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'low': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      default: return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  if (loading) {
    return (
      <div className={`min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-indigo-900 ${className}`}>
        <div className="flex items-center justify-center h-64">
          <div className="flex items-center space-x-3">
            <RefreshCw className="w-6 h-6 text-purple-400 animate-spin" />
            <span className="text-white text-lg">Loading Voicemail Analytics...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-indigo-900 ${className}`}>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">Error Loading Analytics</h3>
            <p className="text-gray-300 mb-4">{error}</p>
            <button
              onClick={fetchAnalytics}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!metrics) return null;

  return (
    <div className={`min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-indigo-900 ${className}`}>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Voicemail Analytics Dashboard</h1>
            <p className="text-gray-300">Campaign: {campaignId}</p>
          </div>
          
          <div className="flex items-center space-x-4">
            {/* Time Range Selector */}
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              className="bg-gray-800/50 border border-gray-700 text-white px-4 py-2 rounded-lg focus:ring-2 focus:ring-purple-500"
            >
              <option value="1h">Last Hour</option>
              <option value="24h">Last 24 Hours</option>
              <option value="7d">Last 7 Days</option>
              <option value="30d">Last 30 Days</option>
            </select>

            {/* Auto Refresh Toggle */}
            <button
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                autoRefresh 
                  ? 'bg-green-600/20 text-green-400 border border-green-500/30' 
                  : 'bg-gray-800/50 text-gray-400 border border-gray-700'
              }`}
            >
              {autoRefresh ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              <span>Auto Refresh</span>
            </button>

            {/* Manual Refresh */}
            <button
              onClick={fetchAnalytics}
              className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              <span>Refresh</span>
            </button>
          </div>
        </div>

        {/* View Selector */}
        <div className="flex space-x-2 bg-gray-800/30 p-1 rounded-lg">
          {[
            { key: 'overview', label: 'Overview', icon: BarChart3 },
            { key: 'audio', label: 'Audio Analysis', icon: Mic },
            { key: 'engagement', label: 'Engagement', icon: Headphones },
            { key: 'carriers', label: 'Carriers', icon: Signal }
          ].map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setSelectedView(key as any)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                selectedView === key
                  ? 'bg-purple-600 text-white'
                  : 'text-gray-300 hover:text-white hover:bg-gray-700/50'
              }`}
            >
              <Icon className="w-4 h-4" />
              <span>{label}</span>
            </button>
          ))}
        </div>

        {/* Real-time Metrics Bar */}
        {realTimeMetrics && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-r from-purple-600/20 to-indigo-600/20 backdrop-blur-sm border border-purple-500/30 rounded-xl p-4"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Activity className="w-5 h-5 text-purple-400" />
                <span className="text-white font-medium">Live Metrics</span>
                <span className="text-xs text-gray-400">
                  Updated: {new Date(realTimeMetrics.timestamp).toLocaleTimeString()}
                </span>
              </div>
              
              <div className="flex items-center space-x-6">
                <div className="text-center">
                  <div className="text-sm text-gray-400">Drop Velocity</div>
                  <div className="text-lg font-semibold text-green-400">
                    {realTimeMetrics.dropVelocity.toFixed(1)}/min
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-sm text-gray-400">Listen Velocity</div>
                  <div className="text-lg font-semibold text-purple-400">
                    {realTimeMetrics.listenVelocity.toFixed(1)}/min
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-sm text-gray-400">Active Now</div>
                  <div className="text-lg font-semibold text-indigo-400">
                    {realTimeMetrics.drops + realTimeMetrics.delivered + realTimeMetrics.listened}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Overview View */}
        {selectedView === 'overview' && (
          <div className="space-y-6">
            {/* Key Metrics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                {
                  title: 'Total Drops',
                  value: formatNumber(metrics.totalDrops),
                  icon: PhoneCall,
                  color: 'purple',
                  trend: '+15%'
                },
                {
                  title: 'Listen Rate',
                  value: formatPercentage(metrics.listenRate),
                  icon: Headphones,
                  color: 'green',
                  trend: '+8.2%'
                },
                {
                  title: 'Completion Rate',
                  value: formatPercentage(metrics.completionRate),
                  icon: CheckCircle,
                  color: 'blue',
                  trend: '+3.7%'
                },
                {
                  title: 'Total Cost',
                  value: formatCurrency(metrics.totalCost),
                  icon: DollarSign,
                  color: 'orange',
                  trend: '-5%'
                }
              ].map((metric, index) => (
                <motion.div
                  key={metric.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-xl p-6 hover:border-gray-600 transition-colors"
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className={`p-3 rounded-lg bg-${metric.color}-500/20`}>
                      <metric.icon className={`w-6 h-6 text-${metric.color}-400`} />
                    </div>
                    <div className={`text-sm font-medium ${
                      metric.trend.startsWith('+') ? 'text-green-400' : 'text-red-400'
                    }`}>
                      {metric.trend}
                    </div>
                  </div>
                  <div className="text-2xl font-bold text-white mb-1">{metric.value}</div>
                  <div className="text-sm text-gray-400">{metric.title}</div>
                </motion.div>
              ))}
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Delivery Performance Chart */}
              <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4">Drop Performance</h3>
                <div className="h-64">
                  <Doughnut
                    data={{
                      labels: ['Delivered', 'Failed', 'Pending'],
                      datasets: [{
                        data: [
                          metrics.delivered, 
                          metrics.failed, 
                          metrics.totalDrops - metrics.delivered - metrics.failed
                        ],
                        backgroundColor: ['#8B5CF6', '#EF4444', '#6B7280'],
                        borderWidth: 0
                      }]
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          position: 'bottom',
                          labels: { color: '#D1D5DB' }
                        }
                      }
                    }}
                  />
                </div>
              </div>

              {/* Listen Behavior Chart */}
              <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4">Listen Behavior</h3>
                <div className="h-64">
                  <Doughnut
                    data={{
                      labels: ['Full Listens', 'Partial Listens', 'No Listen'],
                      datasets: [{
                        data: [
                          metrics.listened - metrics.partialListens,
                          metrics.partialListens,
                          metrics.delivered - metrics.listened
                        ],
                        backgroundColor: ['#10B981', '#F59E0B', '#6B7280'],
                        borderWidth: 0
                      }]
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          position: 'bottom',
                          labels: { color: '#D1D5DB' }
                        }
                      }
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Engagement Metrics */}
            <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Engagement Metrics</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="text-center">
                  <div className="text-2xl font-bold text-purple-400 mb-1">
                    {metrics.engagementScore.toFixed(1)}
                  </div>
                  <div className="text-sm text-gray-400">Engagement Score</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-400 mb-1">
                    {formatDuration(metrics.averageListenDuration)}
                  </div>
                  <div className="text-sm text-gray-400">Avg Listen Duration</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-400 mb-1">
                    {formatCurrency(metrics.costPerListen)}
                  </div>
                  <div className="text-sm text-gray-400">Cost per Listen</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-orange-400 mb-1">
                    {formatCurrency(metrics.costPerDrop)}
                  </div>
                  <div className="text-sm text-gray-400">Cost per Drop</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Audio Analysis View */}
        {selectedView === 'audio' && audioAnalysis && (
          <div className="space-y-6">
            {/* Duration Analysis */}
            <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Duration Analysis</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div className="text-center">
                  <div className="text-2xl font-bold text-purple-400 mb-1">
                    {formatDuration(audioAnalysis.averageDuration)}
                  </div>
                  <div className="text-sm text-gray-400">Average Duration</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-400 mb-1">
                    {formatDuration(audioAnalysis.optimalDuration)}
                  </div>
                  <div className="text-sm text-gray-400">Optimal Duration</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-400 mb-1">
                    {audioAnalysis.qualityMetrics.averageBitrate}kbps
                  </div>
                  <div className="text-sm text-gray-400">Average Bitrate</div>
                </div>
              </div>

              {/* Duration Breakdown Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-700">
                      <th className="text-left py-3 px-4 text-gray-300">Duration Range</th>
                      <th className="text-left py-3 px-4 text-gray-300">Count</th>
                      <th className="text-left py-3 px-4 text-gray-300">Listen Rate</th>
                      <th className="text-left py-3 px-4 text-gray-300">Completion Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {audioAnalysis.durationBreakdown.map((item, index) => (
                      <motion.tr
                        key={item.range}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className="border-b border-gray-800 hover:bg-gray-700/30"
                      >
                        <td className="py-3 px-4 text-white font-medium">{item.range}</td>
                        <td className="py-3 px-4 text-gray-300">{formatNumber(item.count)}</td>
                        <td className="py-3 px-4 text-purple-400">{formatPercentage(item.listenRate)}</td>
                        <td className="py-3 px-4 text-green-400">{formatPercentage(item.completionRate)}</td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Audio Quality Metrics */}
            <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Audio Quality Metrics</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-md font-medium text-white mb-3">Format Distribution</h4>
                  <div className="space-y-3">
                    {audioAnalysis.qualityMetrics.formatDistribution.map((format, index) => (
                      <div key={format.format} className="flex items-center justify-between">
                        <span className="text-gray-300">{format.format.toUpperCase()}</span>
                        <div className="flex items-center space-x-3">
                          <span className="text-sm text-gray-400">{formatNumber(format.count)}</span>
                          <span className="text-sm text-green-400">{formatPercentage(format.deliveryRate)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <h4 className="text-md font-medium text-white mb-3">Quality Metrics</h4>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-300">Average File Size</span>
                      <span className="text-purple-400">{(audioAnalysis.qualityMetrics.averageFileSize / 1024).toFixed(1)}KB</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-300">Average Bitrate</span>
                      <span className="text-blue-400">{audioAnalysis.qualityMetrics.averageBitrate}kbps</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Engagement Analysis View */}
        {selectedView === 'engagement' && engagementAnalysis && (
          <div className="space-y-6">
            {/* Listen Behavior */}
            <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Listen Behavior</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-400 mb-1">
                    {formatNumber(engagementAnalysis.listenBehavior.immediateListens)}
                  </div>
                  <div className="text-sm text-gray-400">Immediate Listens</div>
                  <div className="text-xs text-gray-500">(within 1 hour)</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-yellow-400 mb-1">
                    {formatNumber(engagementAnalysis.listenBehavior.delayedListens)}
                  </div>
                  <div className="text-sm text-gray-400">Delayed Listens</div>
                  <div className="text-xs text-gray-500">(after 1 hour)</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-purple-400 mb-1">
                    {Math.round(engagementAnalysis.listenBehavior.averageTimeToListen)}h
                  </div>
                  <div className="text-sm text-gray-400">Avg Time to Listen</div>
                </div>
              </div>
            </div>

            {/* Completion Analysis */}
            <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Completion Analysis</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-md font-medium text-white mb-3">Listen Completion</h4>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-300">Full Listens</span>
                      <span className="text-green-400">{formatNumber(engagementAnalysis.completionAnalysis.fullListens)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-300">Partial Listens</span>
                      <span className="text-yellow-400">{formatNumber(engagementAnalysis.completionAnalysis.partialListens)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-300">Avg Completion</span>
                      <span className="text-purple-400">{formatPercentage(engagementAnalysis.completionAnalysis.averageCompletionPercentage / 100)}</span>
                    </div>
                  </div>
                </div>
                <div className="h-48">
                  <Bar
                    data={{
                      labels: ['Full', 'Partial', 'None'],
                      datasets: [{
                        label: 'Listen Types',
                        data: [
                          engagementAnalysis.completionAnalysis.fullListens,
                          engagementAnalysis.completionAnalysis.partialListens,
                          metrics.delivered - metrics.listened
                        ],
                        backgroundColor: ['#10B981', '#F59E0B', '#6B7280'],
                        borderWidth: 0
                      }]
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: { display: false }
                      },
                      scales: {
                        x: { ticks: { color: '#D1D5DB' } },
                        y: { ticks: { color: '#D1D5DB' } }
                      }
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Carrier Performance View */}
        {selectedView === 'carriers' && (
          <div className="space-y-6">
            <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Carrier Performance</h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-700">
                      <th className="text-left py-3 px-4 text-gray-300">Carrier</th>
                      <th className="text-left py-3 px-4 text-gray-300">Drops</th>
                      <th className="text-left py-3 px-4 text-gray-300">Delivered</th>
                      <th className="text-left py-3 px-4 text-gray-300">Failed</th>
                      <th className="text-left py-3 px-4 text-gray-300">Delivery Rate</th>
                      <th className="text-left py-3 px-4 text-gray-300">Avg Time</th>
                      <th className="text-left py-3 px-4 text-gray-300">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {carrierPerformance.map((carrier, index) => (
                      <motion.tr
                        key={carrier.carrier}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className="border-b border-gray-800 hover:bg-gray-700/30"
                      >
                        <td className="py-3 px-4 text-white font-medium">{carrier.carrier}</td>
                        <td className="py-3 px-4 text-gray-300">{formatNumber(carrier.drops)}</td>
                        <td className="py-3 px-4 text-green-400">{formatNumber(carrier.delivered)}</td>
                        <td className="py-3 px-4 text-red-400">{formatNumber(carrier.failed)}</td>
                        <td className="py-3 px-4 text-purple-400">{formatPercentage(carrier.deliveryRate)}</td>
                        <td className="py-3 px-4 text-blue-400">{carrier.avgDeliveryTime.toFixed(1)}m</td>
                        <td className="py-3 px-4">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(carrier.status)}`}>
                            {carrier.status}
                          </span>
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Recommendations */}
        {recommendations.length > 0 && (
          <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
              <Target className="w-5 h-5 mr-2 text-purple-400" />
              Recommendations
            </h3>
            <div className="space-y-4">
              {recommendations.slice(0, 3).map((rec, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className={`p-4 rounded-lg border ${getPriorityColor(rec.priority)}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-semibold">{rec.title}</h4>
                    <span className="text-xs px-2 py-1 rounded-full bg-gray-700 text-gray-300">
                      {rec.priority}
                    </span>
                  </div>
                  <p className="text-sm text-gray-300 mb-3">{rec.description}</p>
                  <div className="text-sm">
                    <span className="font-medium text-green-400">Expected Impact: </span>
                    <span className="text-gray-300">{rec.expectedImpact}</span>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VoicemailAnalyticsDashboard; 